import { useState, useLayoutEffect, useEffect, useRef, useMemo, lazy, Suspense } from "react";
import styleHomeLayout from "./HomeLayout.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import defaultLogo from "../../assets/WebTwin.png";

import {
  faEnvelope,
  faChevronDown,
  faGauge,
  faLink,
  faRightFromBracket,
  faSearch,
  faXmark,
  faUser,
  faBars,
  faPlay,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedinIn, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { MdOutlineAirplay, MdLink, MdTour } from "react-icons/md";

import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { UnityRenderer } from "../../Components/public/Loaders";
import { WebUrlIframe } from "../../Components/public/WebUrlIframe";
import { useGetSceneDetail } from "../../api/scenes";
import { useGetBasicSettingsList } from "../../api/settings";
import { useContactUs } from "../../api/contactUs";
import { useGetSearchInHomePage } from "../../api/homePageSearch";
import { useGetConfig } from "../../api/config";
import { useLogout } from "../../api/login";
import { CONFIG } from "../../config";
import { useGetPublicScenes } from "../../api/publicCatalog";
import { Head } from "../../Components/public/Navbar";
import { Footer } from "../../Components/public/Footer";
import { useNavbarLogo } from "../../hooks/useNavbarLogo";
import { canAccessCms } from "../../utils/permissions";
import swal from "sweetalert";
import { toast } from "react-toastify";

const DynamicLighting = lazy(() => import("../../Components/public/DynamicLighting/DynamicLighting"));
const XRIllustration = lazy(() => import("../../Components/public/XRIllustration/XRIllustration"));
const ReactPlayer = lazy(() => import("react-player"));

// ─── Featured-project helpers (pure, no imports needed) ───────────────────────
const compareScenesByOrder = (a, b) =>
  (Number(a?.order) || 0) - (Number(b?.order) || 0);

const resolveFeaturedSceneImage = scene =>
  scene?.image || scene?.thumbnail || scene?.thumbnail_image || "";

const resolveFeaturedAssetUrl = path => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${CONFIG.S3_BASE_URI}/${path}`;
};

const getWebUrlSrc = webUrl => {
  if (!webUrl) return "";
  if (typeof webUrl === "object") return webUrl.url || "";
  return typeof webUrl === "string" ? webUrl : "";
};

const aggregateFeaturedScenes = scenes => {
  if (!Array.isArray(scenes) || scenes.length === 0) return [];
  const scenesById = new Map();
  scenes.forEach(scene => {
    if (!scene?.id || scenesById.has(scene.id)) return;
    scenesById.set(scene.id, {
      ...scene,
      image: resolveFeaturedSceneImage(scene),
    });
  });
  return Array.from(scenesById.values()).sort(compareScenesByOrder);
};
// ────────────────────────────────────────────────────────────────────────────────

const HomeLayout = ({ user }) => {
  const themes = JSON.parse(localStorage.getItem("theme"));
  const token = JSON.parse(localStorage.getItem("Token"));

  const locations = useLocation();

  const navigate = useNavigate();

  const {
    data: config,
    isLoading: isLoadingConfig,
    isSuccess: isSuccessConfig,
  } = useGetConfig();

  // state to handle scene animation play and pause
  const [pauseAnimation, setPauseAnimation] = useState(true);

  // state to handle the sound of scene
  const [muteSound, setMuteSound] = useState(true);

  const [isFocused, setIsFocused] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // states for contact us your name
  const [yourName, setYourName] = useState("");

  // states for contact us your email
  const [yourEmail, setYourEmail] = useState("");

  // states for contact us your message
  const [yourMessage, setYourMessage] = useState("");

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showImmersive, setShowImmersive] = useState(false);
  const [showSlugDropdown, setShowSlugDropdown] = useState(false);
  const [slugId, setSlugId] = useState();
  // Mouse position for orbital system tilt
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // FAQ open index
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Scroll state for navbar glass effect
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const landingOverlayRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const featuredGridRef = useRef(null);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const handleFeaturedGridScroll = () => {
    if (!featuredGridRef.current || !featuredGridRef.current.children.length) return;

    const container = featuredGridRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + (containerRect.width / 2);

    let closestIndex = 0;
    let minDistance = Infinity;

    Array.from(container.children).forEach((child, index) => {
      const childRect = child.getBoundingClientRect();
      const childCenter = childRect.left + (childRect.width / 2);
      const distance = Math.abs(childCenter - containerCenter);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    setCanScrollLeft(closestIndex > 0);
    setCanScrollRight(closestIndex < container.children.length - 1);
  };

  const scrollFeaturedCarousel = (direction) => {
    if (!featuredGridRef.current || !featuredGridRef.current.children.length) return;

    const container = featuredGridRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + (containerRect.width / 2);

    let closestIndex = 0;
    let minDistance = Infinity;

    Array.from(container.children).forEach((child, index) => {
      const childRect = child.getBoundingClientRect();
      const childCenter = childRect.left + (childRect.width / 2);
      const distance = Math.abs(childCenter - containerCenter);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    let targetIndex = direction === "left" ? closestIndex - 1 : closestIndex + 1;
    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= container.children.length) targetIndex = container.children.length - 1;

    const targetElement = container.children[targetIndex];
    if (targetElement) {
      // Calculate scroll position to perfectly center the target element
      const targetScrollLeft = targetElement.offsetLeft - (container.clientWidth / 2) + (targetElement.offsetWidth / 2);

      const startScrollLeft = container.scrollLeft;
      const distance = targetScrollLeft - startScrollLeft;
      const duration = 700; // ms for an ultra-smooth fluid glide
      let startTime = null;

      // Temporarily disable scroll-snap to prevent browser interference during JS animation
      const originalSnap = container.style.scrollSnapType;
      container.style.scrollSnapType = 'none';

      // easeInOutCubic easing function for a buttery smooth start and finish
      const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const animateScroll = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);

        container.scrollLeft = startScrollLeft + distance * easeInOutCubic(progress);

        if (timeElapsed < duration) {
          requestAnimationFrame(animateScroll);
        } else {
          // Restore scroll-snap once perfectly positioned
          container.style.scrollSnapType = originalSnap;
          container.scrollLeft = targetScrollLeft; // ensure exact final pixel
        }
      };

      requestAnimationFrame(animateScroll);
    }
  };

  useEffect(() => {
    if (!isLoadingConfig && config?.data?.immersive_experience === false) {
      setShowImmersive(true);
    } else {
      setShowImmersive(false);
    }
  }, [isLoadingConfig, config]);

  // blocking the user with the role of 'Viewer' to go to CMS side

  // state to toggle share icon element
  const [showShare, setShowShare] = useState(false);
  // state to show which floating icon is clicked
  const [floatingOption, setFloatingOption] = useState("");
  // state to hold search value
  const [searcKey, setSearchKey] = useState("");

  // state to hold the name of scene category
  const [breadCrumb, setBreadCrumb] = useState({});

  const { slug, teleportationId } = useParams();

  //dependent query. only runs when homePageOption?.scene is available
  /* 
    1) checks for the teleportation id
    2) if immersive experience is true scene_id is passed
    3) if immersive experience is false and if there is any associated scene then;  id from associated scene will be passed
    4) if there is no associated scenes then; unity scene id is passed
   */

  const {
    data: sceneDetailApi,
    isLoading,
    isSuccess,
  } = useGetSceneDetail(
    teleportationId
      ? teleportationId
      : config?.data?.immersive_experience === true
        ? config?.data?.immersive_details?.scene_id
        : config?.data?.immersive_experience === false
          ? config?.data?.homepage?.associated_scene?.id
          : null
  );

  useEffect(() => {
    if (sceneDetailApi?.scene_categories?.length) {
      const lastCategory = sceneDetailApi.scene_categories[sceneDetailApi.scene_categories.length - 1];
      setBreadCrumb({
        id: lastCategory?.id,
        name: lastCategory?.name,
        slug: lastCategory?.name?.toLowerCase(),
        scene: sceneDetailApi?.title,
      });
    }
  }, [sceneDetailApi]);

  useEffect(() => {
    if (breadCrumb?.id) {
      setSlugId(breadCrumb.id);
      localStorage.setItem("slug-id", breadCrumb.id);
    }
  }, [breadCrumb?.id]);

  useEffect(() => {
    if (
      locations?.pathname?.startsWith("/sector/") &&
      locations?.state?.id
    ) {
      setSlugId(locations.state.id);
      localStorage.setItem("slug-id", locations.state.id);
    }
  }, [locations?.pathname, locations?.state]);

  useEffect(() => {
    setShowSlugDropdown(false);
  }, [locations?.pathname]);

  const { mutateAsync, isLoading: cotactLoading } = useContactUs();
  const shareIcons = [];
  // api to get the data of the basic settings
  const { data: basicSettingList, isLoading: basicSettingLoading } =
    useGetBasicSettingsList();

  // Search runs only after the user types. An empty key must not hit the API.
  const searchURL = searcKey ? `/web-twinprocms/search?key=${searcKey}` : null;

  // api to get the search result
  const { data: search, isLoading: searchLoading } =
    useGetSearchInHomePage(searchURL);

  const {
    productArray,
    overlayProduct,
    videoArray,
    telportationArray,
    annotationObject,
  } = useMemo(() => {
    if (isLoading || !sceneDetailApi) {
      return {
        productArray: null,
        overlayProduct: null,
        videoArray: null,
        telportationArray: null,
        annotationObject: null,
      };
    }
    try {
      const _productArray = {};
      const _overlayProduct = [];
      sceneDetailApi.product_categories?.forEach(item => {
        if (!item.product_button_id) {
          _overlayProduct.push(item);
        }
        _productArray[item.id] = item.product_button_id;
      });

      const _telportationArray = [];
      const _annotationArray = [];
      const _videoArray = {};
      sceneDetailApi.interactions?.forEach(item => {
        switch (item.action_type) {
          case "Teleportation":
            _telportationArray.push({
              teleportId: item.unity_scene_teleportation_id,
              teleportLink: `/scene/${item.scene}`,
            });
            break;
          case "Video":
            _videoArray[item.unity_scene_video_id] = item.embed_link;
            break;
          case "Annotation":
            _annotationArray.push({
              annotation_id: item.unity_scene_annotation_id,
              action_number: 1,
              title: item.name,
              text: item.name,
            });
            break;
          default:
            break;
        }
      });

      return {
        productArray: _productArray,
        overlayProduct: _overlayProduct,
        videoArray: _videoArray,
        telportationArray: _telportationArray,
        annotationObject: { annotations: _annotationArray },
      };
    } catch (error) {
      console.error("HomeLayout: error calculating scene interaction data", error);
      return {
        productArray: null,
        overlayProduct: null,
        videoArray: null,
        telportationArray: null,
        annotationObject: null,
      };
    }
  }, [isLoading, sceneDetailApi]);
  // if(!isLoading){
  //   console.log(sceneDetailApi, "data");
  //   let _productArray=[];

  //     sceneDetailApi.product_categories.map(item => setProductArray([...productArray, item.product_categories]))
  //     console.log(productArray, 'inside if')

  // }

  const [sceneDetail, setSceneDetail] = useState({
    unitySceneName: "smarthome_reworked1612v1",
    productPanel: ["Products_Cata_SH_15"],
    teleportation: {
      teleportId: "23",
      teleportLink: "/scene/6",
    },
  });

  const { mutateAsync: mutateAsyncLogout, isError, error } = useLogout();
  const handleLogout = () => {
    const storedToken = localStorage.getItem("Token");

    const { refresh_token } = storedToken && JSON.parse(storedToken ?? "");

    mutateAsyncLogout({ refresh: refresh_token }).then(response => {
      localStorage.removeItem("Token");
      navigate("/login");
    });
  };

  const handleIconClick = menu => {
    setFloatingOption(menu);
    setShowShare(prev => !prev);
  };

  const handleLinkShare = (event, site) => {
    event.preventDefault();
    let width = 575,
      height = 400,
      left = (window.innerWidth - width) / 2,
      top = (window.innerHeight - height) / 2,
      opts =
        "status=1" +
        ",width=" +
        width +
        ",height=" +
        height +
        ",top=" +
        top +
        ",left=" +
        left;
    switch (site) {
      case "Linkedin":
        let linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`;
        window.open(linkedInUrl, "Linkedin", opts);
        break;
      case "Twitter":
        let twitterUrl = `https://twitter.com/intent/tweet?url=${window.location.href}&text=3D%20Virtual%20World`;
        window.open(twitterUrl, "Twitter", opts);
        break;
      case "Pinterest":
        let pinterestUrl =
          "http://pinterest.com/pin/create/button/?description=Checkout%20This%20Awesome%20Scene!}";
        window.open(pinterestUrl, "Pinterest", opts);
        break;
      case "Facebook":
        let facebookUrl = `https://www.facebook.com/sharer.php?u=${window.location.origin}`;
        window.open(facebookUrl, "Facebook", opts);
      default:
        console.log("here");
    }
  };

  // [typeof null -> 'object'];
  //  [Object.is('obj', null) -> true/false]
  //to create side effect to the css variables to change according to the theme setting page values
  useLayoutEffect(() => {
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("header_color_primary") &&
      themes?.header_color_primary !== "#000000"
    ) {
      //updating the primary color property of nav bar dynamically
      document.documentElement.style.setProperty(
        "--header-color-primary",
        themes?.header_color_primary
      );
    }
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("header_color_secondary") &&
      themes?.header_color_secondary !== "#000000"
    ) {
      //updating the secondary color property of search bar bg
      document.documentElement.style.setProperty(
        "--header-color-secondary",
        themes?.header_color_secondary
      );
    }
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("header_input_bg_color") &&
      themes?.header_input_bg_color !== "#000000"
    ) {
      //updating the search bg color dynamically
      document.documentElement.style.setProperty(
        "--header-input-bg-color",
        themes?.header_input_bg_color
      );
    }
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("header_input_text_color") &&
      themes?.header_input_text_color !== "#000000"
    ) {
      //updating the text color of search bar dynamically
      document.documentElement.style.setProperty(
        "--header-input-text-color",
        themes?.header_input_text_color
      );
    }
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("dropdown_main_background_primary") &&
      themes?.dropdown_main_background_primary !== "#000000"
    ) {
      //updating the bg primari color of dropdown dynamically
      document.documentElement.style.setProperty(
        "--dropdown-bg-color-primary",
        themes?.dropdown_main_background_primary
      );
    }
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("dropdown_main_background_secondary") &&
      themes?.dropdown_main_background_secondary !== "#000000"
    ) {
      //updating the bg secondary color of dropdown secondary
      document.documentElement.style.setProperty(
        "--dropdown-bg-color-secondary",
        themes?.dropdown_main_background_secondary
      );
    }
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("dropdown_text_color") &&
      themes?.dropdown_text_color !== "#000000"
    ) {
      //updating the text color of dropdown dynamically
      document.documentElement.style.setProperty(
        "--dropdown-text-color",
        themes?.dropdown_text_color
      );
    }

    //updating the fixed social bar bg primary
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("fixed_social_bar_background_primary") &&
      themes?.fixed_social_bar_background_primary !== "#000000"
    ) {
      document.documentElement.style.setProperty(
        "--fixed-Social-bar-bg-primary",
        themes?.fixed_social_bar_background_primary
      );
    }
    //updating the fixed social bar bg secondary
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("fixed_social_bar_background_secondary") &&
      themes?.fixed_social_bar_background_secondary !== "#000000"
    ) {
      document.documentElement.style.setProperty(
        "--fixed-Social-bar-bg-secondary",
        themes?.fixed_social_bar_background_secondary
      );
    }

    // updating the svg icon bg color
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("fixed_social_bar_icon_background") &&
      themes?.fixed_social_bar_icon_background !== "#000000"
    ) {
      document.documentElement.style.setProperty(
        "--fixed-social-bar-icon-bg",
        themes?.fixed_social_bar_icon_background
      );
    }

    // updating the svg icon color
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("fixed_social_bar_icon_color") &&
      themes?.fixed_social_bar_icon_color !== "#000000"
    ) {
      document.documentElement.style.setProperty(
        "--fixed-social-bar-icon-color",
        themes?.fixed_social_bar_icon_color
      );
    }

    // updating the social popover bg primary color
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("social_popover_background_primary") &&
      themes?.social_popover_background_primary !== "#000000"
    ) {
      document.documentElement.style.setProperty(
        "--floating-social-popover-bg-priamry",
        themes?.social_popover_background_primary
      );
    }

    // updating the social popover bg secondary color
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("social_popover_background_secondary") &&
      themes?.social_popover_background_secondary !== "#000000"
    ) {
      document.documentElement.style.setProperty(
        "--floating-social-popover-bg-secondary",
        themes?.social_popover_background_secondary
      );
    }

    // updating the social popover input field bg colors and icons
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("social_popover_input_field_color") &&
      themes?.social_popover_input_field_color !== "#000000"
    ) {
      document.documentElement.style.setProperty(
        "--floating-social-popover-color",
        themes?.social_popover_input_field_color
      );
    }
    // updating the social popover input field bg colors and icons
    if (
      typeof themes !== "undefined" &&
      Object.is(themes, null) === false &&
      themes?.hasOwnProperty("social_popover_text_color") &&
      themes?.social_popover_text_color !== "#000000"
    ) {
      document.documentElement.style.setProperty(
        "--floating-social-popover-form-input-color",
        themes?.social_popover_text_color
      );
    }
  }, [themes]);

  const handleContactUsSubmit = e => {
    e.preventDefault();
    mutateAsync({
      data: {
        full_name: yourName,
        email: yourEmail,
        message: yourMessage,
      },
    }).then(res => {
      swal({
        icon: "success",
        title: "Thank You for reaching out to us. ",
        text: "Our team will get back to you soon.",
        button: {
          text: "OK",
          value: true,
          visible: true,
          className: "swal-btn",
          closeModal: true,
        },
        closeOnClickOutside: "true",
        timer: 4000,
      });
    });
    setYourName("");
    setYourEmail("");
    setYourMessage("");
  };

  // function to handle scene animation play and pause
  const handlePauseAnimation = () => {
    setPauseAnimation(prev => !prev);
    try {
      window.unityInstance.Module.WebCMS.SetAnimationEnabled(pauseAnimation);
    } catch (error) {
      console.log(error, "error");
    }
  };

  // function to handle animation sound mute and play
  const handleAnimationSound = () => {
    setMuteSound(prev => !prev);
    try {
      window.unityInstance.Module.WebCMS.SetAudioEnabled(muteSound);
    } catch (error) {
      console.log(error);
    }
  };

  // condition to map according to the homepage option value, either image or video or scene
  const determineHomepageOption = () => {
    if (teleportationId) {
      // pass teleportation id to unity renderer component
      return 1;
    } else if (config?.data?.homepage?.type === "IMAGE" && !isLoadingConfig) {
      // apply image from homepage option
      return 2;
    } else if (config?.data?.homepage?.type === "VIDEO" && !isLoadingConfig) {
      // apply video from homepage option
      return 3;
    } else if (
      config?.data?.homepage?.type === "SCENE" &&
      config?.data?.homepage?.associated_scene?.id
    ) {
      // apply scene from homepage option
      return 1;
    } else if (
      config?.data?.homepage?.type === "SCENE" &&
      config?.data?.homepage?.name &&
      !isLoadingConfig &&
      isLoading
    ) {
      return 5;
    } else {
      // apply default scene from homepage api
      return 6;
    }
  };

  // variable to hold the value from switch case
  const outcome = determineHomepageOption();

  useEffect(() => { }, [slugId]);

  const getSlugId = id => {
    setSlugId(id);
    localStorage.setItem("slug-id", id);
  };

  const isHomeLanding = locations?.pathname === "/" && !slug;
  const isProjectsRoute = locations?.pathname === "/projects";
  const isSectorRoute = locations?.pathname?.startsWith("/sector/");
  const isSceneRoute = locations?.pathname?.startsWith("/scene/");
  const isAuthenticated =
    typeof token !== "undefined" && Object.is(token, null) === false;
  const browseWithoutLogin = Boolean(config?.data?.browse_without_login);
  const canBrowseProjects = isAuthenticated || browseWithoutLogin;

  // Home and sector use only the public scenes API. Portfolio loads categories separately.
  const { data: publicScenes, isLoading: isLoadingPublicScenes } = useGetPublicScenes({
    enabled: isHomeLanding || isSectorRoute,
    });

  const featuredProjects = useMemo(() => {
    if (!browseWithoutLogin || !Array.isArray(publicScenes)) return [];
    return aggregateFeaturedScenes(
      publicScenes.map(scene => ({
        ...scene,
        categoryName: scene?.categories?.[0]?.name,
      }))
    ).slice(0, 6);
  }, [browseWithoutLogin, publicScenes]);

  const sectorDropdownScenes = useMemo(() => {
    const list = Array.isArray(publicScenes) ? publicScenes : [];
    const categoryId = slugId || localStorage.getItem("slug-id");
    if (categoryId == null || categoryId === "") return [];
    return list
      .filter(scene =>
        (scene?.categories || []).some(
          category => String(category?.id) === String(categoryId)
        )
      )
      .sort(compareScenesByOrder);
  }, [publicScenes, slugId]);

  useEffect(() => {
    // Check initial scroll bounds once featured projects load
    if (featuredProjects.length > 0) {
      // Small timeout to allow grid layout to calculate widths
      const timer = setTimeout(handleFeaturedGridScroll, 100);
      return () => clearTimeout(timer);
    }
  }, [featuredProjects]);
  // ─────────────────────────────────────────────────────────────────────────

  // Logo: cached in localStorage after first resolve; only replaced when backend path changes.
  //  null        → first visit, settings still loading
  //  string url  → cached or freshly resolved logo (or defaultLogo when none configured)
  const logoSrc = useNavbarLogo(
    basicSettingList?.navbar_logo,
    basicSettingLoading,
    defaultLogo
  );

  const hasNavBreadcrumb = !isSceneRoute && (
    !!slug ||
    (!isHomeLanding &&
      !isProjectsRoute &&
      Object.is(breadCrumb, null) === false &&
      !!breadCrumb?.name));

  const scrollToHeroSection = () => {
    // Scroll the landing overlay back to top (position 0) so the hero-viewport
    // fills the full 100vh and the content stays correctly centered — NOT
    // scrollIntoView({block:"start"}) which pins the inner hero-section element
    // to y=0, hiding it behind the fixed navbar.
    if (landingOverlayRef.current) {
      landingOverlayRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleHomeNavigation = event => {
    if (locations?.pathname === "/") {
      event.preventDefault();
      scrollToHeroSection();
      return;
    }

    navigate("/", { state: { scrollToHero: true } });
  };

  const handleContactNavigation = () => {
    setIsMobileMenuOpen(false);
    if (locations?.pathname === "/") {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollToSection: "contact" } });
    }
  };

  const handleComingSoon = () => {
    toast.info("Coming soon — stay tuned!");
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (locations?.pathname === "/" && locations?.state?.scrollToHero) {
      window.requestAnimationFrame(() => {
        scrollToHeroSection();
      });
    }
  }, [locations?.pathname, locations?.state]);

  useEffect(() => {
    if (locations?.pathname === "/" && locations?.state?.scrollToSection) {
      window.requestAnimationFrame(() => {
        const el = document.getElementById(locations.state.scrollToSection);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      });
    }
  }, [locations?.pathname, locations?.state]);

  useEffect(() => {
    const handleMouseMove = e => {
      if (window.innerWidth <= 768) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Attach scroll listener to the landing overlay for nav glass effect
  useEffect(() => {
    const el = landingOverlayRef.current;
    if (!el) return;
    const onScroll = () => setIsNavScrolled(el.scrollTop > 24);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [isHomeLanding]);

  useEffect(() => {
    const onProjectsScrollState = event => {
      if (!isProjectsRoute) return;
      setIsNavScrolled(Boolean(event?.detail?.isScrolled));
    };

    window.addEventListener("projects-scroll-state", onProjectsScrollState);
    return () =>
      window.removeEventListener("projects-scroll-state", onProjectsScrollState);
  }, [isProjectsRoute]);

  useEffect(() => {
    if (!isHomeLanding && !isProjectsRoute) {
      setIsNavScrolled(false);
    }
  }, [isHomeLanding, isProjectsRoute]);

  useEffect(() => {
    const closeDropdownOnOutsideClick = event => {
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target)
      ) {
        setIsAccountDropdownOpen(false);
      }
    };

    const closeDropdownOnEscape = event => {
      if (event.key === "Escape") {
        setIsAccountDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", closeDropdownOnOutsideClick);
    document.addEventListener("touchstart", closeDropdownOnOutsideClick);
    document.addEventListener("keydown", closeDropdownOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeDropdownOnOutsideClick);
      document.removeEventListener("touchstart", closeDropdownOnOutsideClick);
      document.removeEventListener("keydown", closeDropdownOnEscape);
    };
  }, []);

  useEffect(() => {
    setIsAccountDropdownOpen(false);
  }, [locations?.pathname]);

  const handlePlayPublicDemo = () => {
    // Intentionally left as a no-op placeholder.
    navigate("/projects")
  };

  return (getWebUrlSrc(sceneDetailApi?.web_url) && !isLoadingConfig) ? (
    <div className="fixed inset-0 overflow-hidden bg-black">
      <WebUrlIframe src={getWebUrlSrc(sceneDetailApi.web_url)} className="absolute inset-0 w-full h-full border-0" />
    </div>
  ) :
    (
      <main className={styleHomeLayout["home-layout-main-container"]}>
        <Suspense fallback={null}>
          <DynamicLighting />
        </Suspense>
        <Head title={config?.data?.title} favicon={config?.data?.favicon} />
        <section className={styleHomeLayout["home-layout-nav-container"]}>
          <nav className={`${isNavScrolled ? styleHomeLayout["nav-scrolled"] : ""} ${isMobileMenuOpen ? styleHomeLayout["nav-mobile-open"] : ""}`}>
            <div className={styleHomeLayout["logo-search-dropdown-container"]}>
              <div className={styleHomeLayout["logo-home-btn-container"]}>
                {/* LEFT: Brand */}
                <div className={styleHomeLayout["hover-dropdown"]}>
                  <Link to="/" className={styleHomeLayout["brand-link"]}>
                    <figure className={styleHomeLayout["svg-logo-container"]}>
                      {logoSrc === null ? (
                        // Settings API still loading — reserved skeleton keeps layout stable
                        <img
                          src={`${window.location.origin}/assets/${process.env.REACT_APP_LOGO}`}
                          alt="Web Twin logo"
                        />
                      ) : (
                        <img
                          src={logoSrc}
                          alt="Web Twin logo"
                        />
                      )}
                    </figure>
                  </Link>
                </div>

                {/* CENTER: Nav links — always shown on landing page */}
                {!isSceneRoute && (showImmersive === true || isHomeLanding || isProjectsRoute) && (
                  <>
                    <ul className={styleHomeLayout["sector-whole-wrapper"]}>
{ !token && (
                      <li className={styleHomeLayout["home"]}>
                        <Link
                          className={`${styleHomeLayout["primary-nav-link"]} ${isHomeLanding ? styleHomeLayout["nav-link-active"] : ""
                            }`}
                          to="/"
                          onClick={handleHomeNavigation}
                        >
                          Home
                        </Link>
                      </li>
)}
                      {canBrowseProjects && (
                        <li className={styleHomeLayout["home"]}>
                          <Link
                            className={`${styleHomeLayout["primary-nav-link"]} ${isProjectsRoute ? styleHomeLayout["nav-link-active"] : ""
                              }`}
                            to="/projects"
                          >
                            Portfolio
                          </Link>
                        </li>
                      )}
                      { !token && (
                      <li className={styleHomeLayout["home"]}>
                        <Link
                          className={`${styleHomeLayout["primary-nav-link"]} ${locations?.pathname?.startsWith("/resources") ? styleHomeLayout["nav-link-active"] : ""}`}
                          to="/resources"
                        >
                          Resources
                        </Link>
                      </li>
)}
                    </ul>
                    {hasNavBreadcrumb && (
                      <ul className={styleHomeLayout["breadcrumb-nav"]}>
                        <li
                          className={`${styleHomeLayout["slug"]} ${showSlugDropdown
                            ? styleHomeLayout["slug-dropdown-open"]
                            : ""
                            }`}
                        >
                          {slug && <Link>{slug}</Link>}
                          {!slug &&
                            locations?.pathname !== "/" &&
                            locations?.pathname !== "/projects" &&
                            Object.is(breadCrumb, null) === false && (
                              <>
                                <Link
                                  className={
                                    Object.is(breadCrumb, null) === false &&
                                    breadCrumb.name &&
                                    styleHomeLayout["slug-right-arrow"]
                                  }
                                  to={`/sector/${breadCrumb?.slug || ""}`}
                                  state={{ id: breadCrumb?.id }}
                                  onClick={() => {
                                    if (breadCrumb?.id) {
                                      setSlugId(breadCrumb.id);
                                      localStorage.setItem("slug-id", breadCrumb.id);
                                    }
                                    setShowSlugDropdown(prev => !prev);
                                  }}
                                >
                                  {Object.is(sceneDetailApi, null) === false &&
                                    breadCrumb?.name}
                                </Link>
                                <Link
                                  to={`/scene/${sceneDetailApi?.id || teleportationId || ""
                                    }`}
                                >
                                  {breadCrumb?.scene || sceneDetailApi?.title}
                                </Link>
                              </>
                            )}

                          {sectorDropdownScenes.length > 0 &&
                            showSlugDropdown && (
                              <div className={styleHomeLayout["slug-hover-dropdown"]}>
                                {sectorDropdownScenes.map(slugTitle => (
                                  <Link
                                    key={slugTitle?.id}
                                    to={`/scene/${slugTitle?.id}`}
                                  >
                                    <img
                                      style={{ width: "40px", height: "auto" }}
                                      src={`${CONFIG.BASE_URI}${slugTitle?.image}`}
                                      alt="scene_icon"
                                    />
                                    <p>{slugTitle?.title} </p>
                                  </Link>
                                ))}
                              </div>
                            )}
                        </li>
                      </ul>
                    )}
                  </>
                )}

                {/* RIGHT: Access Dashboard / CMS button */}
                {/* RIGHT: Access Dashboard / CMS button */}
                <div className={styleHomeLayout["nav-actions-right"]}>
                  {/* DESKTOP NAV ACTIONS */}
                  {isAuthenticated ? (
                    <div
                      ref={accountDropdownRef}
                      className={`${styleHomeLayout["xr-account-menu"]} ${styleHomeLayout["desktop-only"]}`}
                    >
                      <button
                        type="button"
                        className={`${styleHomeLayout["xr-account-trigger"]} ${isAccountDropdownOpen
                          ? styleHomeLayout["xr-account-trigger-open"]
                          : ""
                          }`}
                        aria-expanded={isAccountDropdownOpen}
                        aria-label="Open account menu"
                        onClick={() =>
                          setIsAccountDropdownOpen(prev => !prev)
                        }
                      >
                        <span className={styleHomeLayout["xr-avatar-ring"]}>
                          <span className={styleHomeLayout["xr-avatar-core"]}>
                            {(user?.email?.[0] || "U").toUpperCase()}
                          </span>
                        </span>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`${styleHomeLayout["xr-account-chevron"]} ${isAccountDropdownOpen
                            ? styleHomeLayout["xr-account-chevron-open"]
                            : ""
                            }`}
                        />
                      </button>

                      <div
                        className={`${styleHomeLayout["xr-account-dropdown"]} ${isAccountDropdownOpen
                          ? styleHomeLayout["xr-account-dropdown-open"]
                          : ""
                          }`}
                      >
                        <span className={styleHomeLayout["xr-account-email"]}>
                          {user?.email}
                        </span>
                        {/* 
                          <div className={styleHomeLayout["xr-account-mobile-nav"]}>
                            ... 
                          </div>
                          */}
                        {canAccessCms(user) && (
                          <Link
                            to="/web-twinprocms"
                            target="_blank"
                            onClick={() => setIsAccountDropdownOpen(false)}
                            className={`${styleHomeLayout["xr-account-action"]} ${styleHomeLayout["xr-account-action-cms"]}`}
                          >
                            <FontAwesomeIcon icon={faGauge} />
                            <span>TwinProcms Pro CMS</span>
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={handleLogout}
                          className={`${styleHomeLayout["xr-account-action"]} ${styleHomeLayout["xr-account-action-logout"]}`}
                        >
                          <FontAwesomeIcon icon={faRightFromBracket} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <Link
                        to="/login"
                        className={`${styleHomeLayout["nav-action-btn-outline"]} ${styleHomeLayout["xr-guest-desktop-action"]}`}
                      >
                        <FontAwesomeIcon icon={faUser} />
                        <span>Login</span>
                      </Link>
                      <button
                        type="button"
                        className={`${styleHomeLayout["nav-action-btn"]} ${styleHomeLayout["xr-guest-desktop-action"]}`}
                        onClick={handleComingSoon}
                      >
                        <span>Sign up</span>
                      </button>
                    </div>
                  )}

                  {/* NEW MOBILE MENU TRIGGER */}
                  <div className={styleHomeLayout["mobile-menu-triggers"]}>
                    {!isAuthenticated && (
                      <button
                        type="button"
                        className={styleHomeLayout["mobile-signup-pill"]}
                        onClick={handleComingSoon}
                      >
                        Sign up
                      </button>
                    )}
                    <button
                      type="button"
                      className={styleHomeLayout["mobile-hamburger-btn"]}
                      onClick={() => setIsMobileMenuOpen(true)}
                      aria-label="Open mobile menu"
                    >
                      <FontAwesomeIcon icon={faBars} />
                    </button>
                  </div>

                  {/* NEW MOBILE MENU OVERLAY */}
                  {isMobileMenuOpen && (
                    <div className={styleHomeLayout["mobile-menu-overlay"]}>
                      <div className={styleHomeLayout["mobile-menu-header"]}>
                        <div className={styleHomeLayout["logo-home-btn-container"]}>
                          <div className={styleHomeLayout["hover-dropdown"]}>
                            <Link to="/" className={styleHomeLayout["brand-link"]} onClick={() => setIsMobileMenuOpen(false)}>
                              <figure className={styleHomeLayout["svg-logo-container"]}>
                                {logoSrc === null ? (
                                  <img
                                    src={`${window.location.origin}/assets/${process.env.REACT_APP_LOGO}`}
                                    alt="WebTwin logo"
                                  />
                                ) : (
                                  <img src={logoSrc} alt="WebTwin logo" />
                                )}
                              </figure>
                            </Link>
                          </div>
                          <div className={styleHomeLayout["mobile-menu-triggers"]}>
                            {!isAuthenticated && (
                              <button
                                type="button"
                                className={styleHomeLayout["mobile-signup-pill"]}
                                onClick={handleComingSoon}
                              >
                                Sign up
                              </button>
                            )}
                            <button
                              type="button"
                              className={styleHomeLayout["mobile-close-btn"]}
                              onClick={() => setIsMobileMenuOpen(false)}
                              aria-label="Close mobile menu"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className={styleHomeLayout["mobile-menu-list"]}>
                        { !token && (
                        <Link
                          to="/"
                          className={`${styleHomeLayout["mobile-menu-item"]} ${isHomeLanding ? styleHomeLayout["mobile-menu-item-active"] : ""}`}
                          onClick={(e) => {
                            handleHomeNavigation(e);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          Home
                        </Link>
                        )}
                        {canBrowseProjects && (
                          <Link
                            to="/projects"
                            className={`${styleHomeLayout["mobile-menu-item"]} ${isProjectsRoute ? styleHomeLayout["mobile-menu-item-active"] : ""}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Portfolio
                          </Link>
                        )}
                        { !token && (
                        <Link
                          to="/resources"
                          className={`${styleHomeLayout["mobile-menu-item"]} ${locations?.pathname?.startsWith("/resources") ? styleHomeLayout["mobile-menu-item-active"] : ""}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Resources
                        </Link>
                        )}
                        {isAuthenticated && (
                          <Link
                            to="/web-twinprocms"
                            target="_blank"
                            className={styleHomeLayout["mobile-menu-item"]}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            TwinProcms Pro CMS
                          </Link>
                        )}
                      </div>

                      <div className={styleHomeLayout["mobile-menu-footer"]}>
                        {isAuthenticated ? (
                          <>
                            <span className={styleHomeLayout["mobile-menu-footer-email"]}>
                              {user?.email}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                handleLogout();
                                setIsMobileMenuOpen(false);
                              }}
                              className={styleHomeLayout["mobile-menu-logout"]}
                            >
                              Logout
                            </button>
                          </>
                        ) : (
                          <>
                            <Link
                              to="/login"
                              className={styleHomeLayout["mobile-menu-login-outline"]}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              Log in
                            </Link>
                            <button
                              type="button"
                              className={styleHomeLayout["mobile-signup-pill"]}
                              onClick={handleComingSoon}
                            >
                              Sign up
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </nav>
          {/* search result displaying element */}
          {!searchLoading &&
            isFocused &&
            (search?.scene_category?.length > 0 ||
              search?.scene?.length > 0 ||
              search?.product?.length > 0) && (
              <>
                {searchLoading ? (
                  <span>Loading..</span>
                ) : (
                  <div className={styleHomeLayout["search-results-dropdown"]}>
                    <ul className={styleHomeLayout["search-result-lists"]}>
                      {search?.scene.map(item => (
                        <div
                          className={styleHomeLayout["whole-result-wrapper"]}
                        >
                          <Link
                            key={item.id}
                            className={styleHomeLayout["search-scene-title"]}
                            to={`scene/${item?.id}`}
                          >
                            {item?.title}
                          </Link>
                          {item?.scene_categories?.length > 0 &&
                            item?.scene_categories.map(sceneCategoriesItem => (
                              <>
                                <Link
                                  className={
                                    styleHomeLayout[
                                    "search-scene-category-name"
                                    ]
                                  }
                                  to={`scene/${sceneCategoriesItem?.id}`}
                                >
                                  {sceneCategoriesItem?.name}
                                </Link>
                              </>
                            ))}

                          <Link to={`scene/${item?.id}`}>
                            {/* <FontAwesomeIcon
                                icon={faArrowRight}
                                style={{
                                  fontSize: "large",
                                }}
                              /> */}
                            <svg
                              width="25"
                              height="25"
                              viewBox="0 0 43 43"
                              fill="#201F2F"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="21.2124"
                                cy="21.0731"
                                r="20.5"
                                stroke="#6FC6E0"
                              ></circle>
                              <path
                                d="M23.128 14.8669L29.0122 21.0736L23.128 27.2802"
                                stroke="#6FC6E0"
                              ></path>
                              <path
                                d="M28.6856 21.0731L13.4126 21.0731"
                                stroke="#6FC6E0"
                              ></path>
                            </svg>
                          </Link>
                        </div>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
        </section>
        {/* display none has been added to hide the share panel on the left */}
        <div className={styleHomeLayout["floating-menu-dropdown-container"]} style={{ display: "none" }}>
          <div className={styleHomeLayout["home-layout-floating-icon"]}>
            {/* Contact us icon */}
            <svg
              width="43"
              height="43"
              viewBox="0 0 43 43"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => handleIconClick("contact")}
            >
              <rect
                x="0.78418"
                y="0.861206"
                width="41"
                height="41.0013"
                rx="20.5"
                stroke="#6FC6E0"
                fill="#121212"
              ></rect>
              <circle
                cx="21.2838"
                cy="16.7077"
                r="4.79273"
                stroke="#6FC6E0"
                strokeWidth="1.2"
              ></circle>
              <path
                d="M15.2342 17.2252H13.3977C13.3977 12.87 16.9283 9.33948 21.2834 9.33948C25.6386 9.33948 29.1692 12.87 29.1692 17.2252H27.0122"
                stroke="#6FC6E0"
                strokeWidth="1.2"
              ></path>
              <path
                d="M15.1841 15.6498V18.7994"
                stroke="#6FC6E0"
                strokeWidth="1.2"
                strokeLinejoin="bevel"
              ></path>
              <path
                d="M27.4206 15.6498V18.7994"
                stroke="#6FC6E0"
                strokeWidth="1.2"
                strokeLinejoin="bevel"
              ></path>
              <path
                d="M31.4489 33.3859C31.4489 27.7719 26.8978 23.2208 21.2838 23.2208C15.6698 23.2208 11.1188 27.7719 11.1188 33.3859"
                stroke="#6FC6E0"
                strokeWidth="1.2"
              ></path>
            </svg>

            {/* share icon */}
            <svg
              width="43"
              height="43"
              viewBox="0 0 43 43"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => handleIconClick("share")}
            >
              <rect
                x="0.78418"
                y="0.861206"
                width="41"
                height="41.0013"
                rx="20.5"
                stroke="#6FC6E0"
                fill="#121212"
              ></rect>
              <path
                d="M28.1725 15.4457L27.7369 15.496C13.7884 15.496 14.3998 21.9859 14.4 29.2181"
                stroke="#6FC6E0"
                strokeWidth="1.2"
              ></path>
              <path
                d="M24 12L28.1053 15.193C28.3233 15.3626 28.3626 15.6767 28.193 15.8947L25 20"
                stroke="#6FC6E0"
                strokeWidth="1.2"
                strokeLinecap="round"
              ></path>
            </svg>
            {sceneDetailApi?.Unity_Scene?.name && (
              <>
                {/* sound off icon */}
                <svg
                  width="43"
                  height="43"
                  viewBox="0 0 43 43"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={handleAnimationSound}
                >
                  <rect
                    x="1.46191"
                    y="1.36401"
                    width="41"
                    height="41"
                    rx="20.5"
                    fill="#121212"
                  />
                  <rect
                    x="1.46191"
                    y="1.36401"
                    width="41"
                    height="41"
                    rx="20.5"
                    fill="url(#paint0_radial_737_22663)"
                  />
                  <rect
                    x="1.46191"
                    y="1.36401"
                    width="41"
                    height="41"
                    rx="20.5"
                    stroke="#6FC6E0"
                  />
                  <path
                    d="M21.1763 31.044V12.684L16.5212 18.1424L11.8652 19.6671L11.8661 21.9948L11.8871 24.0939L16.543 24.86L21.1763 31.044Z"
                    stroke="url(#paint1_linear_737_22663)"
                    strokeWidth="1.2"
                    strokeLinejoin="bevel"
                  />
                  <path
                    d="M24.4692 18.2767V25.2486"
                    stroke="url(#paint2_linear_737_22663)"
                    strokeWidth="1.2"
                    strokeLinejoin="bevel"
                  />
                  <path
                    d="M28.0229 15.6602L28.023 27.8639"
                    stroke="url(#paint3_linear_737_22663)"
                    strokeWidth="1.2"
                    strokeLinejoin="bevel"
                  />
                  <path
                    d="M32.0581 13.4122L32.0581 30.1185"
                    stroke="url(#paint4_linear_737_22663)"
                    strokeWidth="1.2"
                    strokeLinejoin="bevel"
                  />
                  <defs>
                    <radialGradient
                      id="paint0_radial_737_22663"
                      cx="0"
                      cy="0"
                      r="1"
                      gradientUnits="userSpaceOnUse"
                      gradientTransform="translate(21.9619 21.864) rotate(90) scale(29.0371)"
                    >
                      <stop stopColor="#172A4F" />
                      <stop offset="1" stopColor="#172A4F" stopOpacity="0" />
                    </radialGradient>
                    <linearGradient
                      id="paint1_linear_737_22663"
                      x1="16.5208"
                      y1="12.684"
                      x2="21.1763"
                      y2="32.7111"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6FC6E0" />
                      <stop
                        offset="0.50621"
                        stopColor="#00C4FF"
                        stopOpacity="0.760417"
                      />
                      <stop offset="1" stopColor="#6FC6E0" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient
                      id="paint2_linear_737_22663"
                      x1="24.9692"
                      y1="18.2767"
                      x2="29.6635"
                      y2="26.341"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6FC6E0" />
                      <stop offset="1" stopColor="#6FC6E0" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient
                      id="paint3_linear_737_22663"
                      x1="28.5229"
                      y1="15.6602"
                      x2="28.5229"
                      y2="27.8639"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6FC6E0" />
                      <stop offset="1" stopColor="#6FC6E0" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient
                      id="paint4_linear_737_22663"
                      x1="38.2736"
                      y1="30.1193"
                      x2="27.8943"
                      y2="12.4241"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6FC6E0" />
                      <stop offset="1" stopColor="#6FC6E0" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
                {/* animation pause icon */}
                <svg
                  width="43"
                  height="43"
                  viewBox="0 0 43 43"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={handlePauseAnimation}
                >
                  <rect
                    x="1.46191"
                    y="1.36401"
                    width="41"
                    height="41"
                    rx="20.5"
                    fill="#121212"
                  />
                  <rect
                    x="1.46191"
                    y="1.36401"
                    width="41"
                    height="41"
                    rx="20.5"
                    fill="url(#paint0_radial_737_22664)"
                  />
                  <rect
                    x="1.46191"
                    y="1.36401"
                    width="41"
                    height="41"
                    rx="20.5"
                    stroke="#6FC6E0"
                  />
                  <path
                    d="M21.9618 11.099V10.499H21.3618V11.099H21.9618ZM32.1268 21.864C32.1268 27.478 27.5758 32.029 21.9618 32.029V33.229C28.2385 33.229 33.3268 28.1407 33.3268 21.864H32.1268ZM21.9618 32.029C16.3478 32.029 11.7968 27.478 11.7968 21.864H10.5968C10.5968 28.1407 15.6851 33.229 21.9618 33.229V32.029ZM21.9618 11.699C27.5758 11.699 32.1268 16.25 32.1268 21.864H33.3268C33.3268 15.5873 28.2385 10.499 21.9618 10.499V11.699ZM21.3618 11.099V16.1742H22.5618V11.099H21.3618Z"
                    fill="url(#paint1_linear_737_22664)"
                  />
                  <path
                    d="M16.6382 16.517L21.8716 21.7513"
                    stroke="url(#paint2_linear_737_22664)"
                    strokeWidth="1.2"
                    strokeLinejoin="bevel"
                  />
                  <defs>
                    <radialGradient
                      id="paint0_radial_737_22664"
                      cx="0"
                      cy="0"
                      r="1"
                      gradientUnits="userSpaceOnUse"
                      gradientTransform="translate(21.9619 21.864) rotate(90) scale(29.0371)"
                    >
                      <stop stopColor="#172A4F" />
                      <stop offset="1" stopColor="#172A4F" stopOpacity="0" />
                    </radialGradient>
                    <linearGradient
                      id="paint1_linear_737_22664"
                      x1="31.0833"
                      y1="28.9508"
                      x2="11.1968"
                      y2="11.099"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6FC6E0" />
                      <stop
                        offset="0.328696"
                        stopColor="#00C4FF"
                        stopOpacity="0.6875"
                      />
                      <stop offset="1" stopColor="#6FC6E0" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient
                      id="paint2_linear_737_22664"
                      x1="22.6065"
                      y1="22.537"
                      x2="16.6382"
                      y2="17.5403"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6FC6E0" />
                      <stop
                        offset="0.28125"
                        stopColor="#00C4FF"
                        stopOpacity="0.71875"
                      />
                      <stop offset="1" stopColor="#6FC6E0" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
              </>
            )}
          </div>
          {showShare && floatingOption === "share" && (
            <div className={styleHomeLayout["share-icon-container"]}>
              <p>Share this page</p>
              <br />
              <ul>
                {shareIcons?.map(
                  item =>
                    item?.value === true && (
                      <li>
                        {/* displaying icon according to the name */}
                        {item.name === "Facebook" ? (
                          <>
                            <Link
                              onClick={event =>
                                handleLinkShare(event, "Facebook")
                              }
                            >
                              <div className={styleHomeLayout.facebook}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  xmlnsXlink="http://www.w3.org/1999/xlink"
                                  width="21"
                                  height="21"
                                  fill="none"
                                  viewBox="0 0 21 21"
                                >
                                  <path
                                    fill="url(#pattern0)"
                                    d="M0.645 0.066H20.645V20.066H0.645z"
                                  ></path>
                                  <defs>
                                    <pattern
                                      id="pattern0"
                                      width="1"
                                      height="1"
                                      patternContentUnits="objectBoundingBox"
                                    >
                                      <use
                                        transform="scale(.01)"
                                        xlinkHref="#image0_170_36190"
                                      ></use>
                                    </pattern>
                                    <image
                                      id="image0_170_36190"
                                      width="100"
                                      height="100"
                                      xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAABmJLR0QA/wD/AP+gvaeTAAACwElEQVR4nO3dPWtUQRiG4XtCBI1EET9QEREFC0VQsLQXq5Q2gqClH//Azk5LsbaxEEsLEbHLLxBJaZGgwWAKjRGNksciW4jgbnZ3zsw7yXPBFkl2Z95wc/Zs9iwEzMzMzMzMykq1B+iSpJ3AeeAccBw4BEwDk8Av4Fvvrj+B773b595tCVgGPqaUlstOvoVI2ifplqTXktY0vicl558suVmXJB0B7gHXganK44ys+SCSJoC7wH1gd+VxxtZ0EEl7gWfA5dqz5NJsEEkHgTdsnLC3jInaA4xC0jTwii0WAxoNAjwGLtQeogvNBZF0FbhWe46uNBVE0hTwsPYcXWoqCHADOFZ7iC61FuRO7QG61kwQSReB07Xn6FozQYCZ2gOU0NIfhpcyrfMVeArMAgu9r1f63L/fz7YnSUnSSoZ3bmcl7a/9+/TTxPUQSYeBxTGXWQVOpZQ+ZRipM62cQ05kWONF9BjQTpA9GdZ4m2GNzrUSJMcFpy8Z1uhcK0F2ZFijiVdLrQTJ4UftATZjOwVpgoME4yDBOEgwDhKMgwTjIMFUeftd0k3gwBAPyfFxnxlJJ0d43HpK6UGG/Telyru9kt4BZ2vsPYLFlNLRUpv5KWuwhZKbOchg8yU3c5DBfIQE4yDB+CkrGB8hwThIIGtA0Q9GOEh/H1JK6yU3dJD+ip7QwUEGKXr+AAcZxEdIMD5CgnGQYIoHqXU95Ayw669vrbLxmv9/rgCPxtz2NvByyMfMp5R+j7nvUKpcMUwpzQ1zf0lLGbZdSim9z7BOp/yUFYyDBOMgwThIMA4SjIME4yDBOEgwDhKMgwTjIME4SDAOEoyDBOMgwThIMA4SjIME4yDBOEgwDhKMgwTjIME4SDAOEoyDBOMgwThIMA4SjIME4yDBOEgwDhKMgwTjIMG08k/BFoDnGdYwMzMzMzOzf/wBJ7MO+xWD7ZMAAAAASUVORK5CYII="
                                    ></image>
                                  </defs>
                                </svg>
                              </div>
                            </Link>
                          </>
                        ) : item.name === "Twitter" ? (
                          <>
                            <Link
                              onClick={event =>
                                handleLinkShare(event, "Twitter")
                              }
                            >
                              <div className={styleHomeLayout.twitter}>
                                {/* <svg
                              xmlns="http://www.w3.org/2000/svg"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                              width="21"
                              height="21"
                              fill="none"
                              viewBox="0 0 21 21"
                            >
                              <path
                                fill="url(#pattern0)"
                                d="M0.954 0.293H20.954V20.293H0.954z"
                              ></path>
                              <defs>
                                <pattern
                                  id="pattern0"
                                  width="1"
                                  height="1"
                                  patternContentUnits="objectBoundingBox"
                                >
                                  <use
                                    transform="scale(.01)"
                                    xlinkHref="#image0_170_36192"
                                  ></use>
                                </pattern>
                                <image
                                  id="image0_170_36192"
                                  width="100"
                                  height="100"
                                  xlinkHref="data:image/png;base64,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"
                                ></image>
                              </defs>
                            </svg> */}
                                <FontAwesomeIcon
                                  icon={faXTwitter}
                                  style={{ height: "20px", width: "20px" }}
                                />
                              </div>
                            </Link>
                          </>
                        ) : item.name === "Linkedin" ? (
                          <>
                            <Link
                              onClick={event =>
                                handleLinkShare(event, "Linkedin")
                              }
                            >
                              <div className={styleHomeLayout.linkedin}>
                                <FontAwesomeIcon
                                  icon={faLinkedinIn}
                                  style={{ height: "20px", width: "20px" }}
                                />
                              </div>
                            </Link>
                          </>
                        ) : item.name === "Pinterest" ? (
                          <>
                            <Link
                              onClick={event =>
                                handleLinkShare(event, "Pinterest")
                              }
                            >
                              <div className={styleHomeLayout.pinterest}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  version="1.2"
                                  viewBox="0 0 50 50"
                                >
                                  <defs>
                                    <image
                                      id="img1"
                                      width="50"
                                      height="50"
                                      href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyAQMAAAAk8RryAAAAAXNSR0IB2cksfwAAAANQTFRFAAAAp3o92gAAAAF0Uk5TAEDm2GYAAAAOSURBVHicY2AYBYMJAAABkAAB07sQ/gAAAABJRU5ErkJggg=="
                                    ></image>
                                    <image
                                      id="img2"
                                      width="37"
                                      height="49"
                                      href="data:image/png;base64,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"
                                    ></image>
                                  </defs>
                                  <style></style>
                                  <use
                                    id="Background"
                                    x="0"
                                    y="0"
                                    href="#img1"
                                  ></use>
                                  <use
                                    id="Layer copy"
                                    x="6"
                                    y="1"
                                    href="#img2"
                                  ></use>
                                </svg>
                              </div>
                            </Link>
                          </>
                        ) : item.name === "Email" ? (
                          <>
                            <Link
                              to={
                                "mailto:?subject=I wanted you to see this site;body=Check out this site"
                              }
                              target="_blank"
                            >
                              <div className={styleHomeLayout.envelop}>
                                <FontAwesomeIcon
                                  icon={faEnvelope}
                                  style={{ height: "20px", width: "20px" }}
                                />
                              </div>
                            </Link>
                          </>
                        ) : (
                          item.name === "Copy Link" && (
                            <>
                              <div
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  navigator.clipboard.writeText(
                                    `${window.location.href}`
                                  )
                                }
                                className={styleHomeLayout.link}
                              >
                                <FontAwesomeIcon icon={faLink} />
                              </div>
                            </>
                          )
                        )}
                      </li>
                    )
                )}
              </ul>
            </div>
          )}
          {/* Contact Us form */}
          {showShare && floatingOption === "contact" && (
            <div className={styleHomeLayout["contact-us-form-container"]}>
              <FontAwesomeIcon
                icon={faXmark}
                className={styleHomeLayout["contact-us-close-btn"]}
                onClick={() => setShowShare(false)}
              />
              <span className={styleHomeLayout["contact-us"]}>Contact Us</span>
              <p className={styleHomeLayout["contact-number"]}>
                {basicSettingList?.contact_number}
              </p>
              <br />
              <hr />
              <br />
              <form
                className={styleHomeLayout["contact-us-form"]}
                onSubmit={handleContactUsSubmit}
              >
                <input
                  type="text"
                  placeholder="Your Name"
                  onChange={e => setYourName(e.target.value)}
                  value={yourName}
                />
                <input
                  type="text"
                  placeholder="Your Email"
                  onChange={e => setYourEmail(e.target.value)}
                  value={yourEmail}
                />
                <input
                  type="textarea"
                  placeholder="Your message goes here.."
                  onChange={e => setYourMessage(e.target.value)}
                  value={yourMessage}
                />
                <div
                  className={styleHomeLayout["contact-us-submit-btn-container"]}
                >
                  <button type="submit">Send</button>
                </div>
              </form>
            </div>
          )}
        </div>

        {isProjectsRoute ? (
          <Outlet />
        ) : slug && !isLoadingPublicScenes ? (
          <Outlet />
        ) : (
          <section className={styleHomeLayout["home-layout-body-container"]}>
            {!isHomeLanding && (
              <div className={styleHomeLayout["home-layout-body"]}>
                {(() => {
                  switch (outcome) {
                    // teleportation id condition
                    case 1:
                      return (
                        <>
                          {!isLoading && !isLoadingConfig && (
                            <UnityRenderer
                              tabIndex={1}
                              fileName={sceneDetailApi?.Unity_Scene?.name}
                              loadingText={
                                // config?.data?.homepage?.loading_text ||
                                // sceneDetailApi?.Unity_Scene?.loading_text
                                sceneDetailApi &&
                                  sceneDetailApi?.Unity_Scene?.loading_text
                                  ? sceneDetailApi?.Unity_Scene?.loading_text
                                  : basicSettingList?.default_loading_text
                              }
                              loadingImageBg={
                                // `${CONFIG.BASE_URI}/${sceneDetailApi?.Unity_Scene?.background_image}` ||

                                sceneDetailApi?.Unity_Scene?.background_image
                              }
                              loadingImage={sceneDetailApi?.image}
                              productPanel={productArray}
                              teleportation={telportationArray}
                              video={videoArray}
                              annotations={annotationObject}
                              basicSettingList={basicSettingList}
                              basicSettingLoading={basicSettingLoading}
                              sceneDetailApi={sceneDetailApi}
                              productCategories={
                                sceneDetailApi?.product_categories
                              }
                              overlayProduct={overlayProduct}
                            />
                          )}
                        </>
                      );
                    // image on homepage option condition
                    case 2:
                      return (
                        <img
                          src={
                            config?.data?.homepage.image
                              ? `${CONFIG.BASE_URI}${config?.data?.homepage.image}`
                              : `${window.location.origin}/assets/NEOM_TD_UIUX-Landing.png`
                          }
                          alt="default-home-page-img"
                        />
                      );
                    // video on homepage option condition
                    case 3:
                      return (
                        <Suspense fallback={null}>
                          <ReactPlayer
                            width="100%"
                            height="100%"
                            url={config?.data?.homepage?.video_embed_code}
                          />
                        </Suspense>
                      );
                    default:
                      // default scene to display
                      return (
                        <>
                          {(!isLoading || !isLoadingConfig) && (
                            <UnityRenderer
                              tabIndex={1}
                              fileName={config?.data?.homepage?.name}
                              loadingImageBg={
                                // `${window.location.origin}${sceneDetailApi?.Unity_Scene?.background_image}` ||
                                `${CONFIG.BASE_URI}/${config?.data?.homepage?.background_image}`
                              }
                              loadingImage={sceneDetailApi?.image}
                              loadingText={config?.data?.homepage?.loading_text}
                              basicSettingList={basicSettingList}
                              basicSettingLoading={basicSettingLoading}
                              cmsMetadata={false}
                              overlayProduct={overlayProduct}
                              sceneDetailApi={sceneDetailApi}
                            />
                          )}
                        </>
                      );
                  }
                })()}
              </div>
            )}

            {/* NEW LANDING PAGE CONTENT - Only show if locations.pathname === "/" and no slug */}
            {isHomeLanding && (
              <div ref={landingOverlayRef} className={styleHomeLayout["landing-page-overlay"]}>

                {/* Hero viewport — clips the oversized orbital rings, mirrors template .hero */}
                <section className={styleHomeLayout["hero-viewport"]}>

                  {/* 3D ORBITAL SYSTEM — tilt follows mouse */}
                  <div
                    className={styleHomeLayout["xr-orbital-system"]}
                    style={{
                      transform: `rotateY(${mousePos.x * 15}deg) rotateX(${-mousePos.y * 15}deg)`,
                    }}
                  >
                    <div className={`${styleHomeLayout["xr-orbital-ring"]} ${styleHomeLayout["ring-1"]}`}></div>
                    <div className={`${styleHomeLayout["xr-orbital-ring"]} ${styleHomeLayout["ring-2"]}`}></div>
                    <div className={`${styleHomeLayout["xr-orbital-ring"]} ${styleHomeLayout["ring-3"]}`}></div>
                    <div className={`${styleHomeLayout["xr-orbital-ring"]} ${styleHomeLayout["ring-4"]}`}></div>
                    <div className={`${styleHomeLayout["xr-core-node"]} ${styleHomeLayout["node-1"]}`}></div>
                    <div className={`${styleHomeLayout["xr-core-node"]} ${styleHomeLayout["node-2"]}`}></div>
                    <div className={`${styleHomeLayout["xr-core-node"]} ${styleHomeLayout["node-3"]}`}></div>
                  </div>
                  <div className={styleHomeLayout["xr-central-glow"]}></div>

                  <div id="home-hero-section" className={styleHomeLayout["hero-section"]}>
                    {/* <span className={styleHomeLayout["hero-badge"]}>✨ Platform Rendering Engine</span> */}
                    <h1>
                      <span className={styleHomeLayout["hero-title-accent"]}><span></span></span>
                      <span className={styleHomeLayout["mobile-line"]}>The Platform for </span>
                      <span className={styleHomeLayout["mobile-line"]}>Building & Managing </span>
                      <span className={`${styleHomeLayout["hero-title-accent"]} ${styleHomeLayout["mobile-line"]}`}>Digital Twins</span>
                      <span style={{ fontWeight: 500 }}><span className={styleHomeLayout["hero-title-accent"]}></span></span>
                    </h1>
                    <p>Create interactive digital twins, 360° virtual tours, 3D walkthroughs, and AR experiences that help customers explore, understand, and engage—all from one platform.
                    </p>
                    <div className={styleHomeLayout["hero-ctas"]}>
                      {!isAuthenticated && (
                        <button className={styleHomeLayout["hero-btn-secondary"]} onClick={handleComingSoon}>Sign Up</button>
                      )}
                      <button className={styleHomeLayout["hero-btn-primary"]} onClick={handleComingSoon}>Book Demo</button>
                    </div>
                    {/* <span style={{ marginTop: "50px" }} className={styleHomeLayout["hero-badge"]}>✨ Get on your spatial journey</span> */}

                  </div>

                </section>

                <div className={styleHomeLayout["features-section"]}>
                  <h2>Beyond a Viewer — <span style={{ color: "#00ccf7" }}>Your Complete XR Platform</span></h2>
                  <p className={styleHomeLayout["section-subtitle"]}>Everything you need to build, manage, and publish immersive experiences — in one place.</p>
                  <div className={styleHomeLayout["features-grid"]}>
                    {/* Card 1 — Unity Scenes & Digital Twins */}
                    <div className={styleHomeLayout["feature-card"]}>
                      <div className={styleHomeLayout["xr-illustration-wrap"]} aria-hidden="true">
                        <Suspense fallback={null}>
                          <XRIllustration />
                        </Suspense>
                      </div>
                      <div className={styleHomeLayout["feature-icon"]}>
                        <MdOutlineAirplay />
                      </div>
                      <h3>Interactive 3D & Digital Twins</h3>
                      <span className={styleHomeLayout["feature-card-divider"]} />
                      <p>Explore lifelike 3D spaces, right in the browser.</p>
                      <button className={styleHomeLayout["feature-learn-more"]} onClick={() => navigate('/resources/interactive-3d')}>Learn more →</button>
                    </div>
                    {/* Card 2 — Web Connector */}
                    <div className={styleHomeLayout["feature-card"]}>
                      <div className={styleHomeLayout["xr-illustration-wrap"]} aria-hidden="true">
                        <Suspense fallback={null}>
                          <XRIllustration />
                        </Suspense>
                      </div>
                      <div className={styleHomeLayout["feature-icon"]}>
                        <MdLink />
                      </div>
                      <h3>AR Experiences</h3>
                      <span className={styleHomeLayout["feature-card-divider"]} />
                      <p>Let clients place your space in their world — no app to install.</p>
                      <button className={styleHomeLayout["feature-learn-more"]} onClick={() => navigate('/resources/ar-experiences')}>Learn more →</button>
                    </div>
                    {/* Card 3 — 360° Virtual Tours */}
                    <div className={styleHomeLayout["feature-card"]}>
                      <div className={styleHomeLayout["xr-illustration-wrap"]} aria-hidden="true">
                        <Suspense fallback={null}>
                          <XRIllustration />
                        </Suspense>
                      </div>
                      <div className={styleHomeLayout["feature-icon"]}>
                        <MdTour />
                      </div>
                      <h3>360° Virtual Tours</h3>
                      <span className={styleHomeLayout["feature-card-divider"]} />
                      <p>Walk clients through real spaces, from anywhere.</p>
                      <button className={styleHomeLayout["feature-learn-more"]} onClick={() => navigate('/resources/360-tours')}>Learn more →</button>
                    </div>
                    {/* Card 4 — Role-Based Access Control */}
                    <div className={styleHomeLayout["feature-card"]}>
                      <div className={styleHomeLayout["xr-illustration-wrap"]} aria-hidden="true">
                        <Suspense fallback={null}>
                          <XRIllustration />
                        </Suspense>
                      </div>
                      <div className={styleHomeLayout["feature-icon"]}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                      </div>
                      <h3>Role-Based Access Control</h3>
                      <span className={styleHomeLayout["feature-card-divider"]} />
                      <p>Control exactly who can see what.</p>
                      <button className={styleHomeLayout["feature-learn-more"]} onClick={() => navigate('/resources/access-control')}>Learn more →</button>
                    </div>
                    {/* Card 5 — Publish Controls */}
                    <div className={styleHomeLayout["feature-card"]}>
                      <div className={styleHomeLayout["xr-illustration-wrap"]} aria-hidden="true">
                        <Suspense fallback={null}>
                          <XRIllustration />
                        </Suspense>
                      </div>
                      <div className={styleHomeLayout["feature-icon"]}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="22" height="14" rx="7" ry="7"></rect><circle cx="16" cy="12" r="3"></circle></svg>
                      </div>
                      <h3>Publish Controls</h3>
                      <span className={styleHomeLayout["feature-card-divider"]} />
                      <p>Choose what's live, and what's still private.</p>
                      <button className={styleHomeLayout["feature-learn-more"]} onClick={() => navigate('/resources/publish-controls')}>Learn more →</button>
                    </div>
                    {/* Card 6 — Instant Publishing */}
                    <div className={styleHomeLayout["feature-card"]}>
                      <div className={styleHomeLayout["xr-illustration-wrap"]} aria-hidden="true">
                        <Suspense fallback={null}>
                          <XRIllustration />
                        </Suspense>
                      </div>
                      <div className={styleHomeLayout["feature-icon"]}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13.5 10.5L21 3"></path><path d="M16 3h5v5"></path><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"></path></svg>
                      </div>
                      <h3>Instant Updates</h3>
                      <span className={styleHomeLayout["feature-card-divider"]} />
                      <p>Update your content anytime — it's live in seconds.</p>
                      <button className={styleHomeLayout["feature-learn-more"]} onClick={() => navigate('/resources/instant-updates')}>Learn more →</button>
                    </div>
                  </div>
                </div>

                <div className={styleHomeLayout["benefits-section"]}>
                  <h2>Why Interaction Matters</h2>
                  <p className={styleHomeLayout["section-subtitle"]}>Static images aren't enough for clients. True understanding requires interactivity.</p>
                  <div className={styleHomeLayout["benefits-rows"]}>

                    {/* Row 1 — Spatial Understanding (image + text) */}
                    <div className={styleHomeLayout["benefit-row"]}>
                      <div className={styleHomeLayout["benefit-row-image"]}>
                        <img
                          src="/assets/benefits/spatial-understanding.png"
                          alt="Urban digital twin — 3D architectural massing model showing a city district"
                          loading="lazy"
                        />
                      </div>
                      <div className={styleHomeLayout["benefit-row-text"]}>
                        <h3>
                          <span className={styleHomeLayout["benefit-inline-icon"]}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                          </span>
                          Spatial Understanding
                        </h3>
                        <p>Allow users to freely explore complex spaces and fully grasp scale and context.</p>
                      </div>
                    </div>

                    {/* Row 2 — Self-Guided Discovery (pull-quote panel — no unbranded screenshot available) */}
                    <div className={styleHomeLayout["benefit-pullquote"]}>
                      <div className={styleHomeLayout["benefit-pullquote-inner"]}>
                        <span className={styleHomeLayout["benefit-inline-icon"]}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon></svg>
                        </span>
                        <h3>Self-Guided Discovery</h3>
                        <p>Empower clients to investigate specific features at their own pace — no guided walkthrough needed, no app download required.</p>
                      </div>
                    </div>

                    {/* Row 3 — Frictionless Sharing (image + text) */}
                    <div className={styleHomeLayout["benefit-row"]}>
                      <div className={styleHomeLayout["benefit-row-image"]}>
                        <img
                          src="/assets/benefits/frictionless-sharing.png"
                          alt="Interactive 3D urban environment — nighttime walkthrough scene"
                          loading="lazy"
                        />
                      </div>
                      <div className={styleHomeLayout["benefit-row-text"]}>
                        <h3>
                          <span className={styleHomeLayout["benefit-inline-icon"]}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
                          </span>
                          Frictionless Sharing
                        </h3>
                        <p>Send a secure URL. Clients enter the 3D space instantly without downloads.</p>
                      </div>
                    </div>

                  </div>
                </div>

                {browseWithoutLogin && (
                  <div className={styleHomeLayout["features-section"]} style={{ paddingBottom: 0, paddingTop: 0 }}>
                    <h2>
                      Real Projects. <span style={{ color: "#00ccf7" }}>Real Results.</span>
                    </h2>
                    <p className={styleHomeLayout["section-subtitle"]}>
                      Explore some of the immersive experiences built with WebTwin
                    </p>
                  </div>
                )}

                <div className={styleHomeLayout["cta-section"]}>
                  {/* ── Featured Projects (visible only when browse_without_login is true) ── */}
                  {browseWithoutLogin && (
                    <div className={styleHomeLayout["featured-projects-row"]}>

                      {isLoadingPublicScenes ? (
                        <div className={styleHomeLayout["featured-loading"]}>
                          <span className={styleHomeLayout["featured-loading-dot"]} />
                          <span className={styleHomeLayout["featured-loading-dot"]} />
                          <span className={styleHomeLayout["featured-loading-dot"]} />
                        </div>
                      ) : featuredProjects.length > 0 ? (
                        <div className={styleHomeLayout["featured-carousel-container"]}>
                          <div
                            className={styleHomeLayout["featured-grid"]}
                            ref={featuredGridRef}
                            onScroll={handleFeaturedGridScroll}
                          >
                            {featuredProjects.map(item => (
                              <article
                                key={item?.id}
                                className={styleHomeLayout["featured-card"]}
                                onClick={() => navigate(`/scene/${item?.public_id}`)}
                                onKeyDown={event => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    navigate(`/scene/${item?.public_id}`);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label={`Open project: ${item?.title}`}
                              >
                                <div className={styleHomeLayout["featured-image-wrapper"]}>
                                  <img
                                    src={resolveFeaturedAssetUrl(resolveFeaturedSceneImage(item))}
                                    alt={item?.title || "Project"}
                                    className={styleHomeLayout["featured-image"]}
                                  />
                                  <div className={styleHomeLayout["featured-hover-action"]}>
                                    <span><FontAwesomeIcon icon={faPlay} style={{ marginLeft: "3px" }} /></span>
                                  </div>
                                </div>
                                <div className={styleHomeLayout["featured-content"]}>
                                  <h4>{item?.title}</h4>
                                  <div className={styleHomeLayout["featured-tags"]}>
                                    {item?.categoryName && (
                                      <span className={styleHomeLayout["featured-tag"]}>
                                        {item.categoryName}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </article>
                            ))}

                          </div>
                          <div className={styleHomeLayout["featured-carousel-nav"]}>
                            <div className={styleHomeLayout["featured-carousel-pill"]}>
                              <button
                                type="button"
                                className={styleHomeLayout["featured-carousel-arrow"]}
                                onClick={() => scrollFeaturedCarousel("left")}
                                aria-label="Previous project"
                                disabled={!canScrollLeft}
                              >
                                <FontAwesomeIcon icon={faChevronLeft} />
                              </button>
                              <div className={styleHomeLayout["featured-carousel-divider"]} />
                              {!canScrollRight ? (
                                <button
                                  type="button"
                                  className={styleHomeLayout["mobile-nav-view-more"]}
                                  onClick={() => navigate(canBrowseProjects ? "/projects" : "/login")}
                                >
                                  {canBrowseProjects ? "View More →" : "Login →"}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className={styleHomeLayout["featured-carousel-arrow"]}
                                  onClick={() => scrollFeaturedCarousel("right")}
                                  aria-label="Next project"
                                  disabled={!canScrollRight}
                                >
                                  <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}

                  <div
                    className={`${styleHomeLayout["cta-action-container"]} ${
                      !canScrollRight ? styleHomeLayout["cta-visible-mobile"] : ""
                    }`}
                  >
                    <button
                      className={styleHomeLayout["featured-view-more-btn"]}
                      onClick={() =>
                        navigate(canBrowseProjects ? "/projects" : "/login")
                      }
                    >
                      {canBrowseProjects
                        ? "View More →"
                        : "Login to View More →"}
                    </button>
                  </div>
                </div>

                <div id="faq" className={styleHomeLayout["faq-section"]}>
                  <h2>Frequently Asked Questions</h2>
                  <div className={styleHomeLayout["faq-list"]}>
                    {[
                      { q: "What is WebTwin?", a: "A CMS platform for managing immersive 3D, XR, and interactive content at scale." },
                      { q: "Do I need a VR headset to use the platform?", a: "No, all experiences run directly in your browser. No app or headset downloads required." },
                      { q: "What type of content can I manage?", a: "3D scenes, Unity experiences, products, images, videos, hotspots, and interactive elements." },
                      { q: "Can I control who accesses my content?", a: "Yes, user roles and permissions allow fine-grained access control for clients and teams." },
                      { q: "Is this suitable for enterprise use?", a: "Yes, it is designed for scalable business and enterprise applications." },
                    ].map((faq, i) => (
                      <div
                        key={i}
                        className={`${styleHomeLayout["faq-item"]} ${openFaqIndex === i ? styleHomeLayout["faq-item-open"] : ""}`}
                        onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                      >
                        <div className={styleHomeLayout["faq-question"]}>
                          <span>{faq.q}</span>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                        </div>
                        <div className={styleHomeLayout["faq-answer"]}>
                          <p>{faq.a}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div id="contact" className={styleHomeLayout["contact-section"]}>
                  <div className={styleHomeLayout["contact-content"]}>
                    <h2>Get in Touch</h2>
                    <p>Have a project in mind or want to explore how immersive technologies can transform your business? Let's connect.</p>
                  </div>
                  <div className={styleHomeLayout["contact-card"]}>
                    <a href="mailto:developer@saecons.com" className={styleHomeLayout["primary-btn"]}>Contact Us</a>
                    <p>Opens your email client</p>
                  </div>
                </div>

                {/* Footer */}
                <Footer
                  logoSrc={logoSrc}
                  onHomeClick={handleHomeNavigation}
                  portfolioHref={canBrowseProjects ? "/projects" : "/login"}
                  onFaqClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })}
                  onContactClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                  className={styleHomeLayout["footer-margin"]}
                />
              </div>
            )}
          </section>
        )}
      </main>
    );
};

export default HomeLayout;
